import { useState, useEffect } from 'react'

/**
 * Hook para debounce de valores
 *
 * @param value - Valor a ser debounced
 * @param delay - Delay em milissegundos (padrão: 500ms)
 * @returns Valor debounced
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('')
 * const debouncedSearch = useDebounce(searchTerm, 500)
 *
 * // Use debouncedSearch para fazer requisições
 * useEffect(() => {
 *   if (debouncedSearch) {
 *     fetchResults(debouncedSearch)
 *   }
 * }, [debouncedSearch])
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Configurar timer para atualizar o valor debounced
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Limpar o timeout se o valor mudar (cleanup)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
